import { useEffect, useState } from "react";
import { useRecordTrade, type TradeCreate } from "./usePositionMutations";
import { useFxRates } from "./useFxRates";

type TradeKind = TradeCreate["trade_type"];

const TITLES: Record<
  TradeKind,
  { heading: string; priceLabel: string | null }
> = {
  sell: { heading: "💰 Продажа", priceLabel: "Цена продажи clean" },
  redeem: { heading: "🏁 Погашение", priceLabel: "Цена погашения clean" },
  amort: { heading: "✂️ Амортизация", priceLabel: "Цена амортизации clean" },
  coupon: { heading: "🪙 Купон получен", priceLabel: null },
};

export function TradeForm({
  positionId,
  kind,
  currency,
  onClose,
}: {
  positionId: number;
  kind: TradeKind;
  currency: string;
  onClose: () => void;
}) {
  const isSellLike = kind !== "coupon";
  const isFx = currency !== "RUB";
  const sym = currencySymbol(currency);
  const today = new Date().toISOString().slice(0, 10);
  const t = TITLES[kind];

  const [quantity, setQuantity] = useState("");
  const [price, setPrice] = useState("");
  const [accrued, setAccrued] = useState(isSellLike ? "0" : "");
  const [tradeDate, setTradeDate] = useState(today);
  const [fxRate, setFxRate] = useState("");
  const [note, setNote] = useState("");

  // Префилл курса ЦБ для не-RUB бумаги. Историю в fx_rates сейчас не пишем,
  // отдаём сегодняшний spot — для свежей сделки это и есть нужный курс,
  // для старой пользователь правит руками.
  const fxRatesQuery = useFxRates();
  useEffect(() => {
    if (!isFx || fxRate !== "") return;
    const rate = fxRatesQuery.data?.[currency];
    if (rate != null) setFxRate(rate.toString());
  }, [isFx, currency, fxRate, fxRatesQuery.data]);

  const mutation = useRecordTrade();

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const trade: TradeCreate = {
      trade_type: kind,
      quantity: isSellLike ? parseInt(quantity, 10) || 0 : 0,
      price: isSellLike && price ? parseFloat(price) : null,
      accrued: parseFloat(accrued || "0"),
      trade_date: tradeDate,
      fx_rate: isFx && fxRate ? parseFloat(fxRate) : null,
      note: note || null,
    };
    mutation.mutate(
      { positionId, trade },
      { onSuccess: () => onClose() },
    );
  };

  return (
    <form className="trade-form" onSubmit={submit}>
      <b>{t.heading}</b>
      <div className="fields">
        {isSellLike && (
          <label>
            Кол-во
            <input
              type="number"
              required
              min={1}
              step={1}
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              autoFocus
            />
          </label>
        )}
        {isSellLike && t.priceLabel && (
          <label>
            {t.priceLabel} ({sym})
            <input
              type="number"
              required
              step={0.01}
              value={price}
              onChange={(e) => setPrice(e.target.value)}
            />
          </label>
        )}
        <label>
          {isSellLike ? `НКД получено всего (${sym})` : `Купон gross всего (${sym})`}
          <input
            type="number"
            required={!isSellLike}
            step={0.01}
            value={accrued}
            onChange={(e) => setAccrued(e.target.value)}
          />
        </label>
        <label>
          {isSellLike ? "Дата" : "Дата выплаты"}
          <input
            type="date"
            required
            value={tradeDate}
            onChange={(e) => setTradeDate(e.target.value)}
          />
        </label>
        {isFx && (
          <label>
            Курс {currency}/RUB
            <input
              type="number"
              step={0.0001}
              value={fxRate}
              onChange={(e) => setFxRate(e.target.value)}
            />
          </label>
        )}
      </div>
      <label>
        Заметка
        <textarea
          placeholder="опционально"
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
      </label>
      <div className="actions">
        <button type="submit" className="btn primary" disabled={mutation.isPending}>
          {mutation.isPending ? "сохраняю…" : "Сохранить"}
        </button>
        <button type="button" className="btn" onClick={onClose}>
          Отмена
        </button>
        {mutation.isError && (
          <span className="status error">
            ошибка: {String(mutation.error)}
          </span>
        )}
      </div>
    </form>
  );
}

function currencySymbol(c: string): string {
  switch (c) {
    case "RUB":
      return "₽";
    case "USD":
      return "$";
    case "CNY":
      return "¥";
    default:
      return c;
  }
}
